package com.arenaproplay.admin.di

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import com.arenaproplay.admin.data.admin.HttpAdminRepository
import com.arenaproplay.admin.data.auth.AuthRepository
import com.arenaproplay.admin.data.http.HttpClientFactory
import com.arenaproplay.admin.data.me.HttpMeRepository
import com.arenaproplay.admin.domain.admin.AdminRepository
import com.arenaproplay.admin.domain.me.MeRepository
import io.ktor.client.*
import kotlinx.browser.localStorage

class AppContainer {
    val defaultHttpClient: HttpClient by lazy { HttpClientFactory.createDefault() }

    private var authenticatedClient: HttpClient? = null

    /**
     * Backed by Compose [MutableState] so that pages reading
     * [adminRepository] inside a composable get re-composed automatically
     * when the auth client is wired up after a hard refresh (the AppEntry
     * LaunchedEffect calls [createAuthenticatedClients] asynchronously, so
     * the first composition sees null — we need the second one to land).
     */
    private val adminRepoState: MutableState<AdminRepository?> = mutableStateOf(null)
    private val meRepoState: MutableState<MeRepository?> = mutableStateOf(null)

    /**
     * Live admin repository. Null before authentication — callers must check
     * before use (the admin pages live behind an auth guard, so by the time
     * they read this it should be wired). On logout it's cleared again.
     */
    val adminRepository: AdminRepository?
        get() = adminRepoState.value

    /**
     * Live "me" repository — talks to the user-scoped `/v1/...` endpoints
     * (profile, notifications, feed, chats, sports). Same lifecycle as
     * [adminRepository]: null before auth, populated on login, cleared on
     * logout.
     */
    val meRepository: MeRepository?
        get() = meRepoState.value

    fun createAuthenticatedClients(
        accessToken: String,
        refreshToken: String,
        onTokensRefreshed: (accessToken: String, refreshToken: String) -> Unit,
        onAuthFailed: () -> Unit,
    ) {
        authenticatedClient?.close()
        val client =
            HttpClientFactory.createAuthenticated(
                accessToken = accessToken,
                refreshToken = refreshToken,
                onTokensRefreshed = onTokensRefreshed,
                onAuthFailed = onAuthFailed,
            )
        authenticatedClient = client
        adminRepoState.value = HttpAdminRepository(client)
        meRepoState.value = HttpMeRepository(client)
    }

    fun getAuthenticatedClient(): HttpClient {
        return authenticatedClient
            ?: throw IllegalStateException("Authenticated client not initialized. Call createAuthenticatedClients(token) first.")
    }

    fun clearAuthenticatedClients() {
        authenticatedClient?.close()
        authenticatedClient = null
        adminRepoState.value = null
        meRepoState.value = null
    }

    val authRepository: AuthRepository by lazy { AuthRepository(defaultHttpClient) }

    private val SIDEBAR_KEY = "arena.admin.sidebar.open"
    val sidebarOpen: MutableState<Boolean> = mutableStateOf(
        try { localStorage.getItem(SIDEBAR_KEY) != "false" } catch (_: Throwable) { true }
    )

    fun setSidebarOpen(open: Boolean) {
        sidebarOpen.value = open
        try { localStorage.setItem(SIDEBAR_KEY, open.toString()) } catch (_: Throwable) {}
    }

    fun dispose() {
        defaultHttpClient.close()
        clearAuthenticatedClients()
    }
}

val LocalAppContainer = compositionLocalOf<AppContainer> {
    error("AppContainer not provided")
}

@Composable
fun ProvideAppContainer(
    container: AppContainer = remember { AppContainer() },
    content: @Composable () -> Unit,
) {
    CompositionLocalProvider(LocalAppContainer provides container) {
        content()
    }
}
